import { useCallback, useEffect, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useCatalogo, useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Campo, Modale, Paginazione, Riquadro, Scelta, Tabella, totaleDi, type ConTotale,
} from '../componenti/base.tsx'
import { LIMITE, giorno, messaggioErrore } from '../componenti/elenco.tsx'
import { IconaPiu } from '../componenti/Icone.tsx'
import { CampoModulo, etichettaCampo, usaModulo } from '../componenti/modulo.tsx'
import { SceltaComune } from '../componenti/persona-campi.tsx'
import type { ComuneScelto } from '../servizi/regole-anagrafica.ts'
import { opzioniSedi } from '../servizi/sedi.ts'
import { SceltaEnte, type Ente } from './Registro.tsx'

/**
 * Attivita' collettive: corsi, laboratori, eventi.
 *
 * Dal 23/10/2026 (Osservazioni n. 4 della Direzione, punto 4; migrazione 472) l'attivita' porta il
 * progetto scelto fra quelli registrati, gli enti partner scelti dall'anagrafica, il comune in cui si
 * svolge (la sede e' chi la organizza) e i partecipanti al posto dei posti: uomini, donne e minori,
 * facoltativi, e il totale, che con le tre voci e' la loro somma. Un'attivita' registrata si modifica
 * dalla riga, se rientra negli incarichi di chi la apre.
 */

/**
 * Tipologie collettive che non sono corsi (CRIT-struttura-09): materiali e networking non hanno
 * partecipanti ne' un documento rilasciato. La tipologia non porta un attributo che lo dica, quindi
 * l'elenco sta qui.
 */
const SENZA_CAMPI_DA_CORSO = new Set(['COM.MATERIALI', 'COM.NETWORKING'])

const VOCI_PARTECIPANTI = [
  { chiave: 'partecipanti_uomini', etichetta: 'Uomini', parola: 'uomini' },
  { chiave: 'partecipanti_donne', etichetta: 'Donne', parola: 'donne' },
  { chiave: 'partecipanti_minori', etichetta: 'Minori', parola: 'minori' },
] as const

type Partecipanti = {
  partecipanti_uomini: number | null; partecipanti_donne: number | null
  partecipanti_minori: number | null; partecipanti_totale: number | null
}

type Attivita = ConTotale & Partecipanti & {
  id: string; titolo: string; tipologia_codice: string; tipologia: string
  data_inizio: string; data_fine: string | null; ore_totali: string | null
  sede_id: string | null; sede_nome: string | null
  comune_svolgimento: string | null; comune_nome: string | null; comune_provincia: string | null
  progetto_id: string | null; progetto_titolo: string | null; progetto_codice: string | null
  partner: Ente[]
  documento_rilasciato: string | null; note: string | null
  dati: Record<string, unknown>
  /** Vero se gli incarichi di chi guarda permettono di modificarla (PATCH /attivita-collettive/:id). */
  modificabile: boolean
  /** Risposte del modulo della tipologia, con le etichette (D-26). */
  risposte?: { campo: string; etichetta: string; testo: string }[]
}

type ProgettoScelto = { id: string; titolo: string; codice_cidis: string | null }

const nomeEnte = (e: Ente) => (e.sigla ? `${e.sigla} — ${e.nome}` : e.nome)
const nomeProgetto = (p: ProgettoScelto) => `${p.codice_cidis ? `${p.codice_cidis} · ` : ''}${p.titolo}`

/** «5 uomini · 7 donne · 3 minori», con le sole voci indicate. */
function suddivisione(a: Partecipanti): string {
  return VOCI_PARTECIPANTI.filter((v) => a[v.chiave] !== null)
    .map((v) => `${a[v.chiave]} ${v.parola}`).join(' · ')
}

export function AttivitaCollettive() {
  const { puo } = useSessione()
  const [elenco, setElenco] = useState<Attivita[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [aperta, setAperta] = useState<Attivita | 'nuova' | null>(null)
  const [salta, setSalta] = useState(0)

  const carica = useCallback(() => {
    api.get<Attivita[]>(`/attivita-collettive?limite=${LIMITE}&salta=${salta}`)
      .then(setElenco)
      .catch((e) => setErrore(messaggioErrore(e)))
  }, [salta])
  useEffect(carica, [carica])

  const modificabili = !!elenco?.some((a) => a.modificabile)

  return (
    <>
      <Intestazione titolo="Attività collettive"
        sottotitolo="Corsi, laboratori ed eventi: dove si svolgono, con quale progetto e partner, quanti partecipanti."
        azioni={puo('attivita_collettiva.gestisci')
          ? (
            <Bottone variante="primario" icona={<IconaPiu />} onClick={() => { setEsito(null); setAperta('nuova') }}>
              Nuova attività
            </Bottone>
          )
          : undefined} />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      {aperta && (
        <ModuloAttivita attivita={aperta === 'nuova' ? null : aperta} chiudi={() => setAperta(null)}
                        salvata={(testo) => { setAperta(null); setEsito(testo); carica() }} />
      )}

      {!elenco ? <Attesa testo="Caricamento…" /> : (
        <Riquadro denso>
          <Tabella intestazioni={['Titolo', 'Periodo', 'Dove', 'Ore', 'Partecipanti', 'Progetto e partner', 'Dettagli',
            ...(modificabili ? [''] : [])]}
                   vuoto={<Vuoto testo="Nessuna attività collettiva registrata." />}>
            {elenco.map((a) => (
              <tr key={a.id}>
                <td data-etichetta="Titolo" className="voce-principale">
                  {a.titolo}
                  <div className="piccolo tenue">{a.tipologia}</div>
                </td>
                <td data-etichetta="Periodo">{giorno(a.data_inizio)} → {giorno(a.data_fine)}</td>
                <td data-etichetta="Dove">
                  {a.comune_nome
                    ? `${a.comune_nome}${a.comune_provincia ? ` (${a.comune_provincia})` : ''}`
                    : <span className="tenue">—</span>}
                  {a.sede_nome && <div className="piccolo tenue">Organizza {a.sede_nome}</div>}
                </td>
                <td data-etichetta="Ore" className="mono">{a.ore_totali ?? '—'}</td>
                <td data-etichetta="Partecipanti">
                  <span className="mono">{a.partecipanti_totale ?? '—'}</span>
                  {suddivisione(a) && <div className="piccolo tenue">{suddivisione(a)}</div>}
                </td>
                <td data-etichetta="Progetto e partner" className="piccolo">
                  {a.progetto_titolo
                    ? <div>{nomeProgetto({ id: a.progetto_id ?? '', titolo: a.progetto_titolo, codice_cidis: a.progetto_codice })}</div>
                    : <div className="tenue">Nessun progetto</div>}
                  {a.partner.length > 0 && (
                    <ul className="interventi-risposte">
                      {a.partner.map((p) => <li key={p.id}>{nomeEnte(p)}</li>)}
                    </ul>
                  )}
                </td>
                <td data-etichetta="Dettagli" className="piccolo">
                  {a.risposte?.length
                    ? (
                      <ul className="interventi-risposte">
                        {a.risposte.map((r) => <li key={r.campo}>{r.etichetta}: {r.testo}</li>)}
                      </ul>
                    )
                    : <span className="tenue">—</span>}
                </td>
                {modificabili && (
                  <td data-etichetta="">
                    {a.modificabile && (
                      <div className="riga riga-a-destra">
                        <Bottone variante="discreto" dimensione="piccolo" aria-label={`Modifica ${a.titolo}`}
                                 onClick={() => { setEsito(null); setAperta(a) }}>
                          Modifica
                        </Bottone>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </Tabella>
          <Paginazione salta={salta} limite={LIMITE} totale={totaleDi(elenco)}
                       ricevute={elenco.length} cambia={setSalta} />
        </Riquadro>
      )}
    </>
  )
}

/** Campo dell'errore restituito dall'API, per segnarlo accanto al campo oltre che in testa. */
const CAMPO_DI_CODICE: Record<string, string> = {
  PARTECIPANTI: 'partecipanti', PROGETTO: 'progetto', PARTNER: 'partner', COMUNE: 'comune',
  SEDE: 'sede', DATE: 'data_fine', DOCUMENTO: 'documento',
}

const testoDi = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n))

/** Nuova attivita' o modifica di una registrata: stessi campi, la tipologia si sceglie solo alla creazione. */
function ModuloAttivita({ attivita, chiudi, salvata }: {
  attivita: Attivita | null; chiudi: () => void; salvata: (esito: string) => void
}) {
  const { tassonomia, sedi } = useSessione()
  const documenti = useCatalogo('documento_rilasciato')
  const [valori, setValori] = useState(() => ({
    titolo: attivita?.titolo ?? '',
    tipologia_codice: attivita?.tipologia_codice ?? '',
    data_inizio: attivita?.data_inizio ?? new Date().toISOString().slice(0, 10),
    data_fine: attivita?.data_fine ?? '',
    ore_totali: attivita?.ore_totali === null || attivita?.ore_totali === undefined ? '' : String(Number(attivita.ore_totali)),
    sede_id: attivita?.sede_id ?? '',
    progetto_id: attivita?.progetto_id ?? '',
    partecipanti_uomini: testoDi(attivita?.partecipanti_uomini),
    partecipanti_donne: testoDi(attivita?.partecipanti_donne),
    partecipanti_minori: testoDi(attivita?.partecipanti_minori),
    partecipanti_totale: testoDi(attivita?.partecipanti_totale),
    documento_rilasciato: attivita?.documento_rilasciato ?? '',
    note: attivita?.note ?? '',
  }))
  const [comune, setComune] = useState<ComuneScelto>({
    codice: attivita?.comune_svolgimento ?? '',
    testo: attivita?.comune_nome
      ? `${attivita.comune_provincia ? `${attivita.comune_provincia} · ` : ''}${attivita.comune_nome}` : '',
    provincia: attivita?.comune_provincia ?? '',
  })
  const [partner, setPartner] = useState<Ente[]>(attivita?.partner ?? [])
  const [nuovoPartner, setNuovoPartner] = useState<Ente | null>(null)
  const [erroreAggiunta, setErroreAggiunta] = useState<string | null>(null)
  const [dati, setDati] = useState<Record<string, unknown>>(attivita?.dati ?? {})
  const [progetti, setProgetti] = useState<ProgettoScelto[]>([])
  const [errore, setErrore] = useState<{ messaggio: string; campo: string | null } | null>(null)
  const [dettagli, setDettagli] = useState<{ campo: string; messaggio: string }[] | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const cambia = (campo: keyof typeof valori, v: string) => setValori((x) => ({ ...x, [campo]: v }))

  // Tendina dei progetti: quelli a cui chi registra puo' legare un'attivita' (GET /progetti/selezionabili,
  // come per gli interventi). Il progetto gia' registrato resta fra le voci anche se non c'e'.
  useEffect(() => {
    api.get<ProgettoScelto[]>('/progetti/selezionabili?limite=500').then(setProgetti).catch(() => setProgetti([]))
  }, [])
  const vociProgetti = attivita?.progetto_id && attivita.progetto_titolo
    && !progetti.some((p) => p.id === attivita.progetto_id)
    ? [{ id: attivita.progetto_id, titolo: attivita.progetto_titolo, codice_cidis: attivita.progetto_codice }, ...progetti]
    : progetti
  const vociSedi = attivita?.sede_id && !sedi.some((s) => s.id === attivita.sede_id)
    ? [{ valore: attivita.sede_id, etichetta: attivita.sede_nome ?? 'Sede non più attiva' }, ...opzioniSedi(sedi)]
    : opzioniSedi(sedi)

  // La tipologia non cambia sulla modifica: tra le voci c'e' anche quando non e' piu' fra quelle in uso.
  const collettive = (tassonomia?.tipologie ?? [])
    .filter((t) => t.gestione === 'attivita_collettiva'
      && ((t.attiva && !t.storica) || t.codice === attivita?.tipologia_codice))
  const modulo = usaModulo(valori.tipologia_codice)

  // Partecipanti e documento valgono per i corsi: nascosti, non si inviano, cosi' un valore scritto
  // prima di cambiare tipologia non resta sull'attivita'.
  const daCorso = !SENZA_CAMPI_DA_CORSO.has(valori.tipologia_codice)

  const numero = (v: string) => (v.trim() === '' ? null : Number(v))
  const voci = VOCI_PARTECIPANTI.map((v) => numero(valori[v.chiave]))
  const complete = voci.every((v) => v !== null)
  const indicate = voci.filter((v): v is number => v !== null)
  const somma = indicate.reduce((a, b) => a + b, 0)
  const totale = complete ? somma : numero(valori.partecipanti_totale)
  const numeriValidi = [...voci, totale].every((v) => v === null || (Number.isInteger(v) && v >= 0))

  const mancano = [
    ...(!valori.titolo.trim() ? ['il titolo'] : []),
    ...(!valori.tipologia_codice ? ['la tipologia'] : []),
    ...(!valori.data_inizio ? ['la data d’inizio'] : []),
    ...(valori.data_fine && valori.data_inizio && valori.data_fine < valori.data_inizio
      ? ['una fine dopo l’inizio'] : []),
    ...(comune.testo.trim() && !comune.codice ? ['il comune scelto fra i suggerimenti'] : []),
    ...(daCorso && !numeriValidi ? ['partecipanti in numeri interi da zero in su'] : []),
    ...(daCorso && numeriValidi && indicate.length > 0 && !complete && totale === null
      ? ['il totale dei partecipanti'] : []),
    ...(daCorso && numeriValidi && !complete && totale !== null && totale < somma
      ? [`un totale non minore di uomini, donne e minori indicati (${somma})`] : []),
  ]

  function aggiungiPartner() {
    if (!nuovoPartner) return
    if (partner.some((p) => p.id === nuovoPartner.id)) {
      setErroreAggiunta('Questo ente è già fra i partner.')
      return
    }
    setErroreAggiunta(null)
    setPartner((a) => [...a, nuovoPartner])
    setNuovoPartner(null)
  }

  async function salva() {
    setErrore(null); setDettagli(null); setInCorso(true)
    const corpo: Record<string, unknown> = {
      titolo: valori.titolo.trim(),
      data_inizio: valori.data_inizio,
      data_fine: valori.data_fine || null,
      ore_totali: valori.ore_totali ? Number(valori.ore_totali) : null,
      sede_id: valori.sede_id || null,
      progetto_id: valori.progetto_id || null,
      comune_svolgimento: comune.codice || null,
      partner: partner.map((p) => p.id),
      note: valori.note.trim() || null,
      dati,
    }
    if (daCorso) {
      VOCI_PARTECIPANTI.forEach((v, i) => { corpo[v.chiave] = voci[i] })
      corpo.partecipanti_totale = totale
      corpo.documento_rilasciato = valori.documento_rilasciato || null
    }
    try {
      if (attivita) {
        await api.patch(`/attivita-collettive/${attivita.id}`, corpo)
        salvata(`«${corpo.titolo as string}»: modifiche salvate.`)
      } else {
        await api.post('/attivita-collettive', { ...corpo, tipologia_codice: valori.tipologia_codice })
        salvata(`«${corpo.titolo as string}» registrata.`)
      }
    } catch (e) {
      setErrore({
        messaggio: messaggioErrore(e),
        campo: e instanceof ErroreApi && e.codice ? CAMPO_DI_CODICE[e.codice] ?? null : null,
      })
      if (e instanceof ErroreApi) setDettagli(e.dettagli ?? null)
    } finally {
      setInCorso(false)
    }
  }
  const erroreDi = (campo: string) => (errore?.campo === campo ? errore.messaggio : null)

  return (
    <Modale larga titolo={attivita ? `Modifica: ${attivita.titolo}` : 'Nuova attività'} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Lascia stare</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={mancano.length > 0}
                       onClick={() => void salva()}>
                {attivita ? 'Salva' : 'Crea'}
              </Bottone>
            </>}>
      {errore && (
        <Avviso tipo="errore">
          <strong>{errore.messaggio}</strong>
          {dettagli && (
            <ul className="elenco-avviso">
              {dettagli.map((d, i) => <li key={i}>{etichettaCampo(modulo, d.campo)}: {d.messaggio}</li>)}
            </ul>
          )}
        </Avviso>
      )}

      <div className="griglia-modulo">
        <div className="campo-doppia-colonna">
          <Campo etichetta="Titolo" obbligatorio maxLength={300} valore={valori.titolo} cambia={(v) => cambia('titolo', v)} />
        </div>
        <Scelta etichetta="Tipologia" obbligatorio vuota="— seleziona —" valore={valori.tipologia_codice}
                disabilitato={!!attivita}
                aiuto={attivita ? 'La tipologia non si cambia: per un’altra tipologia si registra un’altra attività.' : undefined}
                cambia={(v) => {
                  // Cambiando tipologia le risposte date al modulo della precedente si scartano.
                  setDati({})
                  cambia('tipologia_codice', v)
                }}
                opzioni={collettive.map((t) => ({ valore: t.codice, etichetta: t.nome }))} />
        <Campo etichetta="Inizio" tipo="date" obbligatorio valore={valori.data_inizio}
               cambia={(v) => cambia('data_inizio', v)} />
        <Campo etichetta="Fine" tipo="date" valore={valori.data_fine} errore={erroreDi('data_fine')}
               cambia={(v) => cambia('data_fine', v)} />
        <Campo etichetta="Ore totali" tipo="number" min={0} passi={0.5} valore={valori.ore_totali}
               cambia={(v) => cambia('ore_totali', v)} />
      </div>

      <h3 className="rete-titoletto">Dove e con chi</h3>
      <div className="griglia-modulo">
        <Scelta etichetta="Sede che organizza" vuota="— seleziona —" valore={valori.sede_id}
                cambia={(v) => cambia('sede_id', v)} opzioni={vociSedi} errore={erroreDi('sede')} />
        <SceltaComune etichetta="Comune in cui si svolge" valore={comune} cambia={setComune}
                      errore={erroreDi('comune')}
                      aiuto="Può essere diverso da quello della sede: Varcaturo che organizza a Castel Volturno." />
        <Scelta etichetta="Progetto" vuota="— nessun progetto —" valore={valori.progetto_id}
                cambia={(v) => cambia('progetto_id', v)} errore={erroreDi('progetto')}
                opzioni={vociProgetti.map((p) => ({ valore: p.id, etichetta: nomeProgetto(p) }))} />
      </div>

      <h3 className="rete-titoletto">Partner</h3>
      {partner.length === 0 && <p className="tenue">Nessun partner.</p>}
      {partner.map((p) => (
        <div key={p.id} className="rete-voce-modulo">
          <span className="rete-voce-nome">{nomeEnte(p)}</span>
          <Bottone variante="discreto" dimensione="piccolo" aria-label={`Togli ${nomeEnte(p)} dai partner`}
                   onClick={() => setPartner((a) => a.filter((x) => x.id !== p.id))}>
            Togli
          </Bottone>
        </div>
      ))}
      <SceltaEnte etichetta="Ente da aggiungere fra i partner" vuota="— scegliere un ente —" valore={nuovoPartner}
                  errore={erroreAggiunta ?? erroreDi('partner')}
                  cambia={(e) => { setNuovoPartner(e); setErroreAggiunta(null) }} />
      <div className="riga">
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />} disabled={!nuovoPartner}
                 onClick={aggiungiPartner}>
          Aggiungi il partner
        </Bottone>
      </div>
      <p className="nota-modulo">
        Gli enti si scelgono dall’anagrafica: se un ente manca, chi gestisce gli enti lo registra da «Enti».
      </p>

      {daCorso && (
        <>
          <h3 className="rete-titoletto">Partecipanti</h3>
          <div className="griglia-modulo">
            {VOCI_PARTECIPANTI.map((v) => (
              <Campo key={v.chiave} etichetta={v.etichetta} tipo="number" min={0} passi={1}
                     modalitaTastiera="numeric" valore={valori[v.chiave]} cambia={(x) => cambia(v.chiave, x)} />
            ))}
            <Campo etichetta="Totale" tipo="number" min={0} passi={1} modalitaTastiera="numeric"
                   valore={complete ? String(somma) : valori.partecipanti_totale}
                   disabilitato={complete} errore={erroreDi('partecipanti')}
                   aiuto={complete
                     ? 'È la somma di uomini, donne e minori.'
                     : 'Se non conosci la suddivisione, scrivi solo il totale.'}
                   cambia={(x) => cambia('partecipanti_totale', x)} />
          </div>
          <div className="griglia-modulo">
            <Scelta etichetta="Documento rilasciato" vuota="— nessuno —" valore={valori.documento_rilasciato}
                    errore={erroreDi('documento')}
                    cambia={(v) => cambia('documento_rilasciato', v)}
                    opzioni={documenti.voci.map((d) => ({ valore: d.codice, etichetta: d.nome }))} />
          </div>
        </>
      )}

      {modulo && modulo.campi.length > 0 && (
        <fieldset className="interventi-campi-tipologia">
          <legend>Campi della tipologia</legend>
          <div className="griglia-modulo">
            {modulo.campi.map((c) => (
              <CampoModulo key={c.codice} campo={c} valore={dati[c.codice]}
                           errore={dettagli?.find((d) => d.campo === c.codice)?.messaggio}
                           cambia={(v) => setDati((d) => ({ ...d, [c.codice]: v }))} />
            ))}
          </div>
        </fieldset>
      )}

      <AreaTesto etichetta="Note" righe={3} maxLength={2000} valore={valori.note} cambia={(v) => cambia('note', v)} />
      {mancano.length > 0 && (valori.titolo.trim() || valori.tipologia_codice) && (
        <p className="nota-modulo">Mancano ancora: {mancano.join(', ')}.</p>
      )}
    </Modale>
  )
}
